import { MerchandiseItem } from './Item';
import { Schedule, UseDate } from './Types';

export type OrderSerialize = {
    uid?: string;
    merchandise_item_id: number;
    reserves_room_id: number;
    use_date: string;
    start_time: string;
    end_time: string | null;
    quantity: number;
    schedule_id: number | null;
    select_answer: number | null;
    text_answer?: string;
};

export class Order {
    readonly uid: string;
    readonly merchandise_item_id: number;
    reserves_room_id: number;
    use_date: string;
    start_time: string;
    end_time: string;
    quantity: number;
    schedule_id: number | null;
    select_answer: number | null;
    text_answer: string;

    constructor(
        src: OrderSerialize,
        readonly items: MerchandiseItem[]
    ) {
        this.uid =
            src.uid ??
            [...Array(16)]
                .map(() => Math.floor(Math.random() * 16).toString(16))
                .join('');
        this.merchandise_item_id = src.merchandise_item_id;
        this.reserves_room_id = src.reserves_room_id;
        this.use_date = src.use_date ?? '';
        this.start_time = src.start_time ?? '';
        this.end_time = src.end_time ?? '';
        this.quantity = src.quantity;
        this.schedule_id = src.schedule_id ?? null;
        this.select_answer = src.select_answer ?? null;
        this.text_answer = src.text_answer ?? '';
        if (!this.validate()) throw new Error('Invalid order.');
    }

    toJSON(): OrderSerialize {
        return {
            uid: this.uid,
            merchandise_item_id: this.merchandise_item_id,
            reserves_room_id: this.reserves_room_id,
            use_date: this.use_date,
            start_time: this.start_time,
            end_time: this.end_time,
            quantity: this.quantity,
            schedule_id: this.schedule_id,
            select_answer: this.select_answer,
            text_answer: this.text_answer,
        };
    }

    static asDefault(
        reserves_room_id: number,
        item: MerchandiseItem,
        items: MerchandiseItem[]
    ): Order {
        const useDate = item.use_dates[0];
        const schedule = useDate.schedules?.[0];

        return new this(
            {
                merchandise_item_id: item.id,
                reserves_room_id: reserves_room_id,
                use_date: useDate.date,
                start_time: schedule?.start_time ?? useDate.start_time ?? '',
                end_time: schedule?.end_time ?? useDate.end_time ?? null,
                quantity: 1,
                schedule_id: schedule?.id ?? null,
                select_answer:
                    item.user_defined_pulldown?.options?.[0]?.id ?? null,
                text_answer: '',
            },
            items
        );
    }

    validate(): boolean {
        if (!this.item) return false;
        if (!this.useDate) return false;
        if (this.quantity < 1) return false;
        if (this.quantity > this.stock) return false;
        return true;
    }

    get item(): MerchandiseItem {
        return this.items.find((i) => i.id === this.merchandise_item_id)!;
    }

    get sales_unit() {
        return this.item.sales_unit;
    }

    get unit_price(): number {
        return this.item.unit_price;
    }

    get unit_price_including_tax(): number {
        return this.item.amountIncludingTax();
    }

    get useDate(): UseDate {
        return this.item.use_dates.find((d) => d.date === this.use_date)!;
    }

    set useDate(useDate: UseDate) {
        const d = this.item.use_dates.find((d) => d.date === useDate.date);
        if (!d) return;
        this.quantity = 1;
        this.start_time = d.start_time ?? '';
        this.schedule = d.schedules?.[0] ?? null;
    }

    get schedule(): Schedule | null {
        if (!this.schedule_id) return null;
        return (
            this.useDate?.schedules?.find((s) => s.id === this.schedule_id) ??
            null
        );
    }

    set schedule(schedule: Schedule | null) {
        const s = schedule
            ? this.useDate?.schedules?.find((s) => s.id === schedule.id)
            : null;
        if (!s) {
            this.schedule_id = null;
            return;
        }
        this.schedule_id = s.id ?? null;
        this.quantity = 1;
        this.start_time = s.start_time ?? '';
        this.end_time = s.end_time ?? '';
    }

    get limit_unit(): number {
        return this.item.limit_unit;
    }
    get stock(): number {
        if (this.sales_unit === 'quantity_unit') return 99;
        const room_stock = this.schedule?.rooms?.find(
            (room) => room?.id === this.reserves_room_id
        )?.stock;
        if (room_stock === 0) return 0;
        if (room_stock && this.schedule?.stock)
            return Math.min(room_stock, this.schedule?.stock);
        return this.item?.stock ?? 99;
    }

    get amount(): number {
        return this.unit_price * this.quantity;
    }

    get amount_including_tax(): number {
        return this.unit_price_including_tax * this.quantity;
    }

    increase() {
        if (this.quantity < this.stock) this.quantity += 1;
        return this.quantity;
    }

    decrease() {
        if (this.quantity > 1) this.quantity -= 1;
        return this.quantity;
    }
}
